import Hls from "hls.js";
import type {
  StormcloudVideoPlayerConfig,
  Scte35Marker,
  Id3TagInfo,
} from "../types";
import type { Scte35CueContext } from "./playerTypes";
import {
  parseScte35FromId3,
  parseScte35FromTsPackets,
  normalizeFragmentPayload,
  getFragmentStartSeconds,
  parseCueOutDuration,
  parseCueOutCont,
  parseAttributeList,
  toNumber,
} from "./Scte35Parser";
import type { Scte35CueManager } from "./Scte35CueManager";
import type { AdTimingService } from "./AdTimingService";

export interface HlsEngineCallbacks {
  onManifestParsed(isLive: boolean): Promise<void>;
  onScte35Cue(marker: Scte35Marker, context: Scte35CueContext): void;
  activatePendingCuesForFragment(frag: any): void;
  onFatalDestroy(): void;
  isInAdBreak(): boolean;
  hasPendingAdBreak(): boolean;
}

export class HlsEngine {
  hls: Hls | undefined;
  isLiveStream: boolean = false;
  nativeHlsMode: boolean = false;
  videoSrcProtection: string | null = null;

  private bufferedSegmentsCount: number = 0;
  private shouldAutoplayAfterBuffering: boolean = false;
  private hasInitialBufferCompleted: boolean = false;

  private readonly config: StormcloudVideoPlayerConfig;
  private readonly video: HTMLVideoElement;
  private readonly cueManager: Scte35CueManager;
  private readonly timing: AdTimingService;
  private readonly callbacks: HlsEngineCallbacks;

  constructor(
    config: StormcloudVideoPlayerConfig,
    video: HTMLVideoElement,
    cueManager: Scte35CueManager,
    timing: AdTimingService,
    callbacks: HlsEngineCallbacks
  ) {
    this.config = config;
    this.video = video;
    this.cueManager = cueManager;
    this.timing = timing;
    this.callbacks = callbacks;
  }

  private get debug(): boolean {
    return !!this.config.debugAdTiming;
  }

  shouldUseNativeHls(getStreamType: () => "hls" | "other"): boolean {
    const streamType = getStreamType();
    if (streamType === "other") {
      return true;
    }
    const canNative = this.video.canPlayType("application/vnd.apple.mpegurl");
    return !!(this.config.allowNativeHls && canNative);
  }

  async setupNativeHls(): Promise<void> {
    this.nativeHlsMode = true;
    this.videoSrcProtection = this.config.src;
    this.video.src = this.config.src;

    this.isLiveStream = this.config.isLiveStream ?? (this.config.lowLatencyMode ?? false);

    if (this.debug) {
      const adBehavior = this.isLiveStream
        ? "live (main video continues muted during ads)"
        : "vod (main video pauses during ads)";
      console.log(
        "[StormcloudVideoPlayer] Using native HLS playback:",
        {
          isLive: this.isLiveStream,
          allowNativeHls: this.config.allowNativeHls,
          adBehavior,
        }
      );
    }

    if (this.config.autoplay) {
      await this.video.play()?.catch(() => {});
    }
  }

  setupHls(): void {
    this.hls = new Hls({
      enableWorker: true,
      backBufferLength: 30,
      liveDurationInfinity: true,
      lowLatencyMode: !!this.config.lowLatencyMode,
      maxLiveSyncPlaybackRate: this.config.lowLatencyMode ? 1.5 : 1.0,
      ...(this.config.lowLatencyMode ? { liveSyncDuration: 2 } : {}),
      maxBufferLength: 30,
      maxMaxBufferLength: 600,
      maxBufferSize: 60 * 1000 * 1000,
      maxBufferHole: 0.5,
      highBufferWatchdogPeriod: 2,
      nudgeOffset: 0.1,
      nudgeMaxRetry: 3,
      startPosition: -1,
    });

    this.hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      this.hls?.loadSource(this.config.src);
    });

    this.hls.on(Hls.Events.MANIFEST_PARSED, async (_, _data: any) => {
      this.isLiveStream =
        this.config.isLiveStream ??
        (this.hls?.levels?.some(
          (level) =>
            level?.details?.live === true || level?.details?.type === "LIVE"
        ) ?? false);

      await this.callbacks.onManifestParsed(this.isLiveStream);

      this.bufferedSegmentsCount = 0;
      this.hasInitialBufferCompleted = false;
      this.shouldAutoplayAfterBuffering = !!this.config.autoplay;

      const minSegments = this.config.minSegmentsBeforePlay ?? 2;

      if (this.debug) {
        console.log(
          "[StormcloudVideoPlayer] Waiting for",
          minSegments,
          "segments to buffer before playback"
        );
      }

      if (minSegments === 0 || !this.config.autoplay) {
        this.hasInitialBufferCompleted = true;
        if (this.config.autoplay) {
          await this.video.play()?.catch(() => {});
        }
      }
    });

    this.hls.on(Hls.Events.LEVEL_LOADED, (_evt, data: any) => {
      if (this.callbacks.isInAdBreak() || this.callbacks.hasPendingAdBreak()) {
        return;
      }

      const details = data?.details;
      if (!details || !details.fragments || details.fragments.length === 0) {
        return;
      }

      const fragmentsToScan = Math.min(5, details.fragments.length);

      for (let i = 0; i < fragmentsToScan; i++) {
        const frag = details.fragments[i];
        const tagList: any[] | undefined = frag?.tagList;

        if (!Array.isArray(tagList)) continue;

        for (const entry of tagList) {
          let tag = "";
          let value = "";

          if (Array.isArray(entry)) {
            tag = String(entry[0] ?? "");
            value = String(entry[1] ?? "");
          } else if (typeof entry === "string") {
            const idx = entry.indexOf(":");
            if (idx >= 0) {
              tag = entry.substring(0, idx);
              value = entry.substring(idx + 1);
            } else {
              tag = entry;
            }
          }

          if (!tag) continue;

          if (tag.includes("EXT-X-CUE-OUT") || tag.includes("EXT-X-DATERANGE")) {
            const attrs = tag.includes("EXT-X-DATERANGE")
              ? parseAttributeList(value)
              : {};
            const hasScteOut = tag.includes("EXT-X-CUE-OUT") ||
              "SCTE35-OUT" in attrs ||
              attrs["SCTE35-OUT"] !== undefined;

            if (hasScteOut) {
              const durationSeconds = parseCueOutDuration(value);
              const marker: Scte35Marker = {
                type: "start",
                ...(durationSeconds !== undefined ? { durationSeconds } : {}),
                raw: { tag, value, earlyDetection: true },
              } as Scte35Marker;

              if (this.debug) {
                console.log("[StormcloudVideoPlayer] EARLY SCTE-35 DETECTION: Ad break marker found in fragment", i, "- starting pre-fetch (NOT playing yet)");
              }

              this.callbacks.onScte35Cue(marker, {
                ...this.cueManager.createScte35CueContext("manifest", "early", frag),
              });
              return;
            }
          }
        }
      }
    });

    this.hls.on(Hls.Events.FRAG_BUFFERED, async (_evt, _data: any) => {
      if (this.hasInitialBufferCompleted) {
        return;
      }

      this.bufferedSegmentsCount++;
      const minSegments = this.config.minSegmentsBeforePlay ?? 2;

      if (this.debug) {
        console.log(
          `[StormcloudVideoPlayer] Buffered segment ${this.bufferedSegmentsCount}/${minSegments}`
        );
      }

      if (this.bufferedSegmentsCount >= minSegments) {
        this.hasInitialBufferCompleted = true;

        if (this.shouldAutoplayAfterBuffering) {
          if (this.debug) {
            console.log(
              `[StormcloudVideoPlayer] Initial buffer complete (${this.bufferedSegmentsCount} segments). Starting playback.`
            );
          }
          await this.video.play()?.catch((err) => {
            if (this.debug) {
              console.warn("[StormcloudVideoPlayer] Autoplay failed:", err);
            }
          });
        }
      }
    });

    this.hls.on(Hls.Events.FRAG_PARSING_METADATA, (_evt, data: any) => {
      const id3Tags: Id3TagInfo[] = (data?.samples || []).map((s: any) => ({
        key: "ID3",
        value: s?.data,
        ptsSeconds: s?.pts,
      }));
      id3Tags.forEach((tag) => this.onId3Tag(tag));
    });

    this.hls.on(Hls.Events.FRAG_LOADED, (_evt, data: any) => {
      this.processFragmentScte35Payload(
        data?.payload,
        typeof data?.frag?.sn === "number" ? data.frag.sn : undefined,
        getFragmentStartSeconds(data?.frag)
      );
    });

    this.hls.on(Hls.Events.FRAG_DECRYPTED, (_evt, data: any) => {
      this.processFragmentScte35Payload(
        data?.payload,
        typeof data?.frag?.sn === "number" ? data.frag.sn : undefined,
        getFragmentStartSeconds(data?.frag)
      );
    });

    this.hls.on(Hls.Events.FRAG_CHANGED, (_evt, data: any) => {
      const frag = data?.frag;
      const tagList: any[] | undefined = frag?.tagList;
      if (!Array.isArray(tagList)) {
        this.callbacks.activatePendingCuesForFragment(frag);
        return;
      }

      for (const entry of tagList) {
        let tag = "";
        let value = "";
        if (Array.isArray(entry)) {
          tag = String(entry[0] ?? "");
          value = String(entry[1] ?? "");
        } else if (typeof entry === "string") {
          const idx = entry.indexOf(":");
          if (idx >= 0) {
            tag = entry.substring(0, idx);
            value = entry.substring(idx + 1);
          } else {
            tag = entry;
            value = "";
          }
        }

        if (!tag) continue;
        if (tag.includes("EXT-X-CUE-OUT-CONT")) {
          const prog = parseCueOutCont(value);
          const marker: Scte35Marker = {
            type: "progress",
            ...(prog?.duration !== undefined
              ? { durationSeconds: prog.duration }
              : {}),
            ...(prog?.elapsed !== undefined
              ? { ptsSeconds: prog.elapsed }
              : {}),
            raw: { tag, value },
          } as Scte35Marker;
          this.callbacks.onScte35Cue(marker, {
            ...this.cueManager.createScte35CueContext("manifest", "playback", frag),
          });
        } else if (tag.includes("EXT-X-CUE-OUT")) {
          const durationSeconds = parseCueOutDuration(value);
          const marker: Scte35Marker = {
            type: "start",
            ...(durationSeconds !== undefined ? { durationSeconds } : {}),
            raw: { tag, value },
          } as Scte35Marker;
          this.callbacks.onScte35Cue(marker, {
            ...this.cueManager.createScte35CueContext("manifest", "playback", frag),
          });
        } else if (tag.includes("EXT-X-CUE-IN")) {
          this.callbacks.onScte35Cue(
            { type: "end", raw: { tag, value } },
            {
              ...this.cueManager.createScte35CueContext("manifest", "playback", frag),
            }
          );
        } else if (tag.includes("EXT-X-DATERANGE")) {
          const attrs = parseAttributeList(value);
          const hasScteOut =
            "SCTE35-OUT" in attrs || attrs["SCTE35-OUT"] !== undefined;
          const hasScteIn =
            "SCTE35-IN" in attrs || attrs["SCTE35-IN"] !== undefined;
          const klass = String(attrs["CLASS"] ?? "");
          const duration = toNumber(attrs["DURATION"]);

          if (hasScteOut || /com\.apple\.hls\.cue/i.test(klass)) {
            const marker: Scte35Marker = {
              type: "start",
              ...(duration !== undefined ? { durationSeconds: duration } : {}),
              raw: { tag, value, attrs },
            } as Scte35Marker;
            this.callbacks.onScte35Cue(marker, {
              ...this.cueManager.createScte35CueContext("manifest", "playback", frag),
            });
          }
          if (hasScteIn) {
            this.callbacks.onScte35Cue(
              { type: "end", raw: { tag, value, attrs } },
              {
                ...this.cueManager.createScte35CueContext("manifest", "playback", frag),
              }
            );
          }
        }
      }
      this.callbacks.activatePendingCuesForFragment(frag);
    });

    this.hls.on(Hls.Events.ERROR, (_evt, data) => {
      if (data?.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            this.hls?.startLoad();
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            this.hls?.recoverMediaError();
            break;
          default:
            this.callbacks.onFatalDestroy();
            break;
        }
      }
    });

    this.hls.attachMedia(this.video);
  }

  private onId3Tag(tag: Id3TagInfo): void {
    if (typeof tag.ptsSeconds === "number") {
      this.timing.updatePtsDrift(tag.ptsSeconds);
    }
    const marker = parseScte35FromId3(tag);
    if (marker) {
      this.callbacks.onScte35Cue(marker, {
        source: "id3",
        readiness: "playback",
      });
    }
  }

  private processFragmentScte35Payload(
    payloadData: unknown,
    fragmentSn?: number,
    fragmentStartSeconds?: number
  ): void {
    const payload = normalizeFragmentPayload(payloadData);
    if (!payload) {
      return;
    }

    const markers = parseScte35FromTsPackets(
      payload,
      this.cueManager.tsScte35Pids,
      this.cueManager.pmtPids,
      this.cueManager.processedTsScte35Sections
    );
    markers.forEach((marker) => {
      const context: Scte35CueContext = {
        source: "ts",
        readiness: "early",
      };
      if (fragmentSn !== undefined) {
        context.fragmentSn = fragmentSn;
      }
      if (fragmentStartSeconds !== undefined) {
        context.fragmentStartSeconds = fragmentStartSeconds;
      }
      this.callbacks.onScte35Cue(marker, context);
    });
  }

  getLiveSyncPosition(): number | undefined {
    const pos = this.hls?.liveSyncPosition;
    return typeof pos === "number" && Number.isFinite(pos) ? pos : undefined;
  }

  restartLoadAt(position: number): void {
    if (!this.hls) {
      return;
    }
    try {
      this.hls.stopLoad();
      this.hls.startLoad(position);
    } catch (error) {
      if (this.debug) {
        console.warn("[StormcloudVideoPlayer] Failed to restart HLS load at live edge:", error);
      }
    }
  }

  destroy(): void {
    this.hls?.destroy();
    this.hls = undefined;
  }
}
